import { useCallback, useState } from "react";
import { toast } from "react-toastify";
import { buildRemovePayload } from "../payloads/buildRemovePayload";
import { webinarService } from "../services/webinarService";
import { canTransitionTo } from "../status/getSessionActions";
import { WebinarServiceError } from "../types/dto";
import { SessionStatus, WebinarSession } from "../types/models";
import { copySessionDetails } from "../utils/copySessionDetails";
import { copyToClipboard } from "../utils/copyToClipboard";
import { logWebinarPayload } from "../utils/logWebinarPayload";
import { shareWebinarSession } from "../utils/shareSession";

type Options = {
  /** Called after a successful mutation (e.g. refresh list). */
  onSuccess?: () => void | Promise<void>;
};

function sessionActionStatus(
  session: Pick<WebinarSession, "status"> & { backendStatus?: SessionStatus }
): SessionStatus {
  return session.backendStatus ?? session.status;
}

/**
 * Shared session write operations: cancel, delete draft, reinstate, copy helpers.
 * Used by list page and session drawer.
 */
export function useSessionMutations({ onSuccess }: Options = {}) {
  const [submitting, setSubmitting] = useState(false);

  const afterSuccess = useCallback(async () => {
    await onSuccess?.();
  }, [onSuccess]);

  const cancelSession = useCallback(
    async (
      session: Pick<WebinarSession, "id" | "status"> & {
        backendStatus?: SessionStatus;
      },
      opts: { reason: string; note?: string }
    ) => {
      setSubmitting(true);
      try {
        const from = sessionActionStatus(session);
        if (!canTransitionTo(from, "cancelled")) {
          throw new WebinarServiceError(
            "INVALID_TRANSITION",
            `Cannot cancel a ${from} session`
          );
        }
        const payload = buildRemovePayload(session, {
          reason: opts.reason,
          note: opts.note || "",
        });
        logWebinarPayload("cancel session", payload);
        await webinarService.removeSession(payload);
        toast.success("Session cancelled");
        await afterSuccess();
        return true;
      } catch (e) {
        toast.error(
          e instanceof WebinarServiceError
            ? e.message
            : e instanceof Error
            ? e.message
            : "Could not cancel session"
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [afterSuccess]
  );

  const deleteDraft = useCallback(
    async (
      session: Pick<WebinarSession, "id" | "status"> & {
        backendStatus?: SessionStatus;
      }
    ) => {
      setSubmitting(true);
      try {
        const from = sessionActionStatus(session);
        if (from !== "draft") {
          throw new WebinarServiceError(
            "INVALID_TRANSITION",
            `Cannot delete a ${from} session`
          );
        }
        const payload = buildRemovePayload(session);
        logWebinarPayload("delete draft", payload);
        await webinarService.removeSession(payload);
        toast.success("Draft deleted");
        await afterSuccess();
        return true;
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Could not delete draft"
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [afterSuccess]
  );

  const reinstateSession = useCallback(
    async (
      session: Pick<WebinarSession, "id" | "status"> & {
        backendStatus?: SessionStatus;
      }
    ) => {
      setSubmitting(true);
      try {
        const from = sessionActionStatus(session);
        if (!canTransitionTo(from, "draft")) {
          throw new WebinarServiceError(
            "INVALID_TRANSITION",
            `Cannot reinstate a ${from} session`
          );
        }
        const payload = { id: session.id };
        logWebinarPayload("reinstate session", payload);
        await webinarService.reinstateSession(payload);
        toast.success("Reinstated as draft — pick a new date");
        await afterSuccess();
        return true;
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Could not reinstate session"
        );
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [afterSuccess]
  );

  const copyJoinLink = useCallback(async (session: WebinarSession) => {
    const joinUrl = (session.joinUrl || "").trim();
    if (!joinUrl) {
      toast.error("No join URL set for this session");
      return false;
    }
    const ok = await copyToClipboard(joinUrl);
    if (ok) {
      toast.success(
        "Join link copied — it stays hidden from members until T‑15 min"
      );
      return true;
    }
    toast.error("Could not copy join URL");
    return false;
  }, []);

  const copyDetails = useCallback(async (session: WebinarSession) => {
    const ok = await copySessionDetails(session);
    toast[ok ? "success" : "error"](
      ok ? "Session details copied" : "Could not copy details"
    );
    return ok;
  }, []);

  const shareSession = useCallback(async (session: WebinarSession) => {
    const result = await shareWebinarSession(session);
    if (result === "shared") {
      toast.success("Share sheet opened");
      return true;
    }
    if (result === "copied") {
      toast.success("App session link copied");
      return true;
    }
    if (result === "cancelled") return false;
    toast.error("Could not share session link");
    return false;
  }, []);

  return {
    submitting,
    cancelSession,
    deleteDraft,
    reinstateSession,
    copyJoinLink,
    copyDetails,
    shareSession,
  };
}
